/**
 * Typed fetch client for the freeAlbum HTTP API (docs/01-build-plan.md §5.5, docs/06-api.md).
 * Every call returns parsed JSON or throws `ApiError`. All request/response shapes come from
 * `@freealbum/shared` so the editor and the server can never drift.
 */
import type { AlbumDocument, ExportOptions, PhotoInfo } from '@freealbum/document';
import type { BackupDiff } from '@freealbum/backup';
import type {
  AdminAlbumsResponse,
  AdminJobsResponse,
  AdminStatsResponse,
  AdminUserDto,
  AdminAuditResponse,
  AdminStorageResponse,
  AdminTrendsResponse,
  AdminUsersResponse,
  AlbumDto,
  AlbumSummary,
  ContributeInfoResponse,
  CreateAlbumRequest,
  ErrorCode,
  ExportJobDto,
  ExportStatus,
  FontDto,
  GetAlbumResponse,
  GoogleConfigResponse,
  GooglePickerSessionResponse,
  GooglePickerSessionStatus,
  ImportRequest,
  MeResponse,
  PresignFile,
  PresignResponse,
  ShareKind,
  ShareLinkDto,
  UserRole,
  ViewResponse,
  PhotoTagsRequest,
  PhotoTagsResponse,
} from '@freealbum/shared';

export type PresignedUpload = PresignResponse['uploads'][number];
export type ImportSource = ImportRequest['source'];
export type ImportItem = ImportRequest['items'][number];
export type AdminJobDto = AdminJobsResponse['jobs'][number];
export type AdminAlbumDto = AdminAlbumsResponse['albums'][number];

/** `overwrite` replaces the album the backup came from; `copy` always makes a new one. */
export type RestoreMode = 'overwrite' | 'copy';

/**
 * What `POST /api/backup/inspect` found in a `.fab` file. Nothing has been written at this point —
 * `existing` is the album the backup came from when this account still has it, and `null` when the
 * original is gone or was never here, in which case only `copy` is possible.
 */
export interface BackupInspectResponse {
  album: { id: string; title: string; lang: 'he' | 'en'; themeId: string; format: string };
  createdAt: string;
  photoCount: number;
  spreadCount: number;
  existing: { albumId: string; diff: BackupDiff } | null;
}

export interface RestoreResponse {
  albumId: string;
  mode: RestoreMode;
  photosRestored: number;
  photosReused: number;
}

/**
 * `{ error: { code, message } }` plus the extra top-level fields some codes carry —
 * `VERSION_CONFLICT` returns the server's current `version` and `doc`.
 */
export interface ApiErrorPayload {
  error: { code: ErrorCode | string; message: string; details?: unknown };
  version?: number;
  doc?: AlbumDocument;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorPayload | null;
  constructor(status: number, body: ApiErrorPayload | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? (status === 401 ? 'UNAUTHORIZED' : status === 404 ? 'NOT_FOUND' : 'UNKNOWN');
    this.body = body;
  }
}

async function request<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  }).catch(() => {
    // A network failure has no status; the error hook maps status 0 to "no connection".
    throw new ApiError(0, null, 'NETWORK');
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) throw new ApiError(res.status, (data as ApiErrorPayload) ?? null, `${res.status} ${res.statusText}`);
  return data as T;
}

const q = (params: Record<string, string | number | undefined | null>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
};

export const api = {
  me: () => request<MeResponse>('/api/me'),

  albums: {
    list: (signal?: AbortSignal) => request<{ albums: AlbumSummary[] }>('/api/albums', { signal }),
    create: (body: CreateAlbumRequest) =>
      request<{ album: AlbumDto; doc: AlbumDocument; version: number }>('/api/albums', { method: 'POST', json: body }),
    get: (id: string) => request<GetAlbumResponse>(`/api/albums/${id}`),
    patch: (id: string, body: { doc?: AlbumDocument; title?: string; version: number }) =>
      request<{ version: number }>(`/api/albums/${id}`, { method: 'PATCH', json: body }),
    delete: (id: string) => request<void>(`/api/albums/${id}`, { method: 'DELETE' }),
    duplicate: (id: string) => request<{ album: AlbumDto }>(`/api/albums/${id}/duplicate`, { method: 'POST' }),
    extendRetention: (id: string) => request<{ purgeAt: string | null }>(`/api/albums/${id}/retention/extend`, { method: 'POST' }),
  },

  photos: {
    list: (albumId: string) => request<{ photos: PhotoInfo[] }>(`/api/albums/${albumId}/photos`),
    recover: (albumId: string) => request<{ photos: PhotoInfo[] }>(`/api/albums/${albumId}/photos/recover`, { method: 'POST' }),
    presign: (albumId: string, files: PresignFile[], resumePhotoId?: string) =>
      request<PresignResponse>(`/api/albums/${albumId}/photos/presign`, { method: 'POST', json: { files, ...(resumePhotoId ? { resumePhotoId } : {}) } }),
    complete: (albumId: string, photoId: string) =>
      request<{ photo: PhotoInfo }>(`/api/albums/${albumId}/photos/${photoId}/complete`, { method: 'POST' }),
    remove: (albumId: string, photoId: string) => request<void>(`/api/albums/${albumId}/photos/${photoId}`, { method: 'DELETE' }),
    import: (albumId: string, source: ImportSource, items: ImportItem[]) =>
      request<{ photos: PhotoInfo[] }>(`/api/albums/${albumId}/photos/import`, { method: 'POST', json: { source, items } }),
    /** Tag or untag many photos at once; the answer carries only the lists that changed. */
    setTags: (albumId: string, body: PhotoTagsRequest) =>
      request<PhotoTagsResponse>(`/api/albums/${albumId}/photos/tags`, { method: 'PATCH', json: body }),
  },

  google: {
    config: () => request<GoogleConfigResponse>('/api/google/config'),
    photosSession: (albumId?: string) => request<GooglePickerSessionResponse>(`/api/google/photos/session${q({ albumId })}`, { method: 'POST' }),
    photosSessionState: (id: string) => request<GooglePickerSessionStatus>(`/api/google/photos/session/${id}`),
  },

  exports: {
    create: (albumId: string, options: ExportOptions, version?: number) =>
      request<{ job: ExportJobDto }>(`/api/albums/${albumId}/exports`, { method: 'POST', json: { options, version } }),
    listForAlbum: (albumId: string, signal?: AbortSignal) => request<{ jobs: ExportJobDto[] }>(`/api/albums/${albumId}/exports`, { signal }),
    get: (jobId: string) => request<{ job: ExportJobDto }>(`/api/exports/${jobId}`),
    cancel: (jobId: string) => request<{ job: ExportJobDto }>(`/api/exports/${jobId}/cancel`, { method: 'POST' }),
  },

  /**
   * Album backups. Both calls send the raw `.fab` bytes as the request body and can run to hundreds
   * of megabytes, so they go through XHR to get real upload progress.
   */
  backup: {
    inspect: (file: Blob, onProgress?: (fraction: number) => void, signal?: AbortSignal) =>
      postFileWithProgress<BackupInspectResponse>('/api/backup/inspect', file, onProgress, signal),
    restore: (file: Blob, mode: RestoreMode, onProgress?: (fraction: number) => void, signal?: AbortSignal) =>
      postFileWithProgress<RestoreResponse>(`/api/backup/restore?mode=${mode}`, file, onProgress, signal),
  },

  share: {
    create: (albumId: string, kind: ShareKind) =>
      request<{ link: ShareLinkDto }>(`/api/albums/${albumId}/share`, { method: 'POST', json: { kind } }),
    list: (albumId: string) => request<{ links: ShareLinkDto[] }>(`/api/albums/${albumId}/share`),
    remove: (id: string) => request<void>(`/api/share/${id}`, { method: 'DELETE' }),
  },

  contribute: {
    info: (token: string) => request<ContributeInfoResponse>(`/api/contribute/${token}`),
    presign: (token: string, contributorName: string, files: PresignFile[]) =>
      request<PresignResponse>(`/api/contribute/${token}/presign`, { method: 'POST', json: { contributorName, files } }),
    complete: (token: string, photoId: string) =>
      request<{ photo: PhotoInfo }>(`/api/contribute/${token}/${photoId}/complete`, { method: 'POST' }),
  },

  /**
   * The viewer payload. `ViewResponse` carries `lang` (the album's CONTENT language, which decides
   * which way the flipbook turns — not the UI locale) and `downloadUrl` (presigned `album.pdf`, or
   * null once that export has expired, in which case the viewer hides the download button).
   */
  view: (token: string) => request<ViewResponse>(`/api/view/${token}`),

  fonts: {
    list: () => request<{ fonts: FontDto[] }>('/api/fonts'),
    upload: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return request<{ font: FontDto }>('/api/fonts', { method: 'POST', body: fd });
    },
    remove: (id: string) => request<void>(`/api/fonts/${id}`, { method: 'DELETE' }),
  },

  admin: {
    stats: () => request<AdminStatsResponse>('/api/admin/stats'),
    users: (params: { q?: string; page?: number; guests?: 'show' | 'hide' }) => request<AdminUsersResponse>(`/api/admin/users${q(params)}`),
    patchUser: (id: string, body: { retentionUnlimited?: boolean; role?: UserRole }) =>
      request<{ user: AdminUserDto }>(`/api/admin/users/${id}`, { method: 'PATCH', json: body }),
    resetPassword: (id: string, password: string) =>
      request<{ ok: true; sessionsRevoked: number }>(`/api/admin/users/${id}/password`, { method: 'POST', json: { password } }),
    jobs: (status?: ExportStatus | '') => request<{ jobs: AdminJobDto[] }>(`/api/admin/jobs${q({ status })}`),
    retryJob: (id: string) => request<{ job: AdminJobDto }>(`/api/admin/jobs/${id}/retry`, { method: 'POST' }),
    cancelJob: (id: string) => request<{ job: AdminJobDto }>(`/api/admin/jobs/${id}/cancel`, { method: 'POST' }),
    albums: (purgeBefore: string) => request<AdminAlbumsResponse>(`/api/admin/albums${q({ purgeBefore })}`),
    storage: () => request<AdminStorageResponse>('/api/admin/storage'),
    trends: (days?: number) => request<AdminTrendsResponse>(`/api/admin/trends${q({ days })}`),
    audit: (params: { action?: string; page?: number }) => request<AdminAuditResponse>(`/api/admin/audit${q(params)}`),
  },
};

/** Upload a file to a presigned PUT URL with progress (XHR, since fetch has no upload progress). */
export function uploadToPresignedUrl(
  upload: PresignedUpload,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', upload.url, true);
    const headers = upload.headers ?? {};
    let hasContentType = false;
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
      if (k.toLowerCase() === 'content-type') hasContentType = true;
    }
    if (!hasContentType && file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed (network)'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
}

/**
 * POST a file as the raw request body, reporting upload progress (XHR — `fetch` cannot).
 *
 * Rejects with the same `ApiError` the JSON client throws, so a 400 from a corrupt backup still
 * carries the server's own explanation instead of a generic failure.
 */
export function postFileWithProgress<T>(
  url: string,
  file: Blob,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, (data as ApiErrorPayload) ?? null, `${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new ApiError(0, null, 'NETWORK'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
}

/**
 * The album's page-flip link, reusing the one that is already out there when we can.
 *
 * `POST /api/albums/:id/share` always mints a NEW row, and a flipbook row is pinned server-side to
 * whichever export was newest at that moment — so an older link keeps serving an older PDF. The DTO
 * does not carry that job id, but because the pin is always "newest finished export", a link created
 * at or after `exportFinishedAt` necessarily points at that export. Anything older (or an unreadable
 * list) means we mint a fresh one rather than hand back a link to yesterday's file.
 */
export async function ensureFlipbookLink(albumId: string, exportFinishedAt?: string | null): Promise<ShareLinkDto> {
  const floor = exportFinishedAt ? Date.parse(exportFinishedAt) : Number.NaN;
  if (Number.isFinite(floor)) {
    try {
      const { links } = await api.share.list(albumId);
      const reusable = links
        .filter((l) => l.kind === 'flipbook' && Date.parse(l.createdAt) >= floor)
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
      if (reusable) return reusable;
    } catch {
      /* listing is a nicety — creating the link is the part that has to work */
    }
  }
  const { link } = await api.share.create(albumId, 'flipbook');
  return link;
}

/**
 * The `.fab` download for an album. A plain link: the route sets `Content-Disposition`, so the
 * browser names and saves the file itself.
 */
export const backupDownloadUrl = (albumId: string): string => `/api/albums/${albumId}/backup`;

export const BACKUP_EXT = '.fab';
export const BACKUP_ACCEPT = '.fab,application/vnd.freealbum.backup';

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];

/**
 * What a photo `<input type="file">` puts in its `accept` — `image/*`, and deliberately nothing more.
 *
 * This is not a validation rule. `partitionFiles` decides what is actually allowed, on every path
 * including drag-and-drop, and says so per file. This string exists only to tell a *picker* what to
 * offer, and on Android the two goals conflict.
 *
 * Chrome for Android hands the input to the system photo picker — the one with a working Albums tab
 * — only when it can convert `accept` cleanly into photo types. This used to read
 * `.jpg,.jpeg,.png,.heic,.heif,.webp`; the HEIC and HEIF entries fail that conversion, so Chrome
 * fell back to the generic documents picker instead. There, Google Photos appears as a storage
 * provider whose album list is empty, which is what Android users were reporting: Google Photos is
 * offered, and it shows no albums even though they have plenty.
 *
 * So: no extension list, no extra MIME types, and no `capture` attribute — each of those sends it
 * back to the documents picker. Anything unsupported that a person picks anyway is refused by name
 * when they pick it, which is a better trade than a picker that cannot find their albums.
 */
export const PHOTO_INPUT_ACCEPT = 'image/*';
/**
 * What a photo input accepts on Android, through `usePhotoInputAccept`. Chrome hands an image-only
 * `accept` (PHOTO_INPUT_ACCEPT above) to the system photo picker, which returns at most
 * `MediaStore.getPickImagesMaxLimit()` — 100 — and no page can raise that. One non-image type sends
 * it to the Files app instead, which takes any number; the price is that Google Photos albums show
 * empty there, and the Google Photos import is the way to those. Octet-stream rather than a file
 * extension because Chrome turns a known extension (".heic") back into an image type on some
 * devices. Files that are not photos are still refused by `partitionFiles`.
 */
export const FILES_INPUT_ACCEPT = 'image/*,application/octet-stream';
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

/**
 * What a name says the type is. The only evidence available for a file that has no `type` — one
 * read out of a ZIP, or a HEIC that iOS handed over with the field empty.
 */
export function mimeForName(name: string): string {
  const ext = name.toLowerCase().split('.').pop();
  if (ext === 'heic') return 'image/heic';
  if (ext === 'heif') return 'image/heif';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return 'application/octet-stream';
}

/** iOS hands HEIC files over with an empty `type`, so the extension is the fallback. */
export function guessMime(file: File): string {
  return file.type || mimeForName(file.name);
}
